import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

export type AttachmentEntityType = 'meeting' | 'offener_punkt';

export interface Attachment {
  id: string;
  entity_type: AttachmentEntityType;
  entity_id: string;
  filename: string;
  mime: string | null;
  size: number | null;
  created_at: string;
}

const KEY = (pid: string, type: string, id: string) =>
  ['projects', pid, 'attachments', type, id] as const;

/** Liste der Anhaenge einer Entitaet (Meeting oder offener Punkt). */
export function useAttachments(
  projectId: string | undefined,
  entityType: AttachmentEntityType,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey: KEY(projectId ?? '', entityType, entityId ?? ''),
    queryFn: () =>
      apiClient.get<Attachment[]>(`/api/v1/projects/${projectId}/attachments`, {
        entity_type: entityType,
        entity_id: entityId,
      }),
    enabled: !!projectId && !!entityId,
  });
}

export function useUploadAttachment(pid: string, entityType: AttachmentEntityType, entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      form.append('entity_type', entityType);
      form.append('entity_id', entityId);
      return apiClient.postForm<Attachment>(`/api/v1/projects/${pid}/attachments`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(pid, entityType, entityId) }),
  });
}

export function useDeleteAttachment(pid: string, entityType: AttachmentEntityType, entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/${pid}/attachments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(pid, entityType, entityId) }),
  });
}

/** Laedt einen Anhang als Blob und stoesst den Browser-Download an. */
export async function downloadAttachment(pid: string, att: Attachment): Promise<void> {
  const blob = await apiClient.requestBlob('GET', `/api/v1/projects/${pid}/attachments/${att.id}/download`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = att.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
